import type * as T from './types';

export interface ClientOptions {
  baseUrl: string; // e.g. http://localhost:8000/api
  getToken: () => string | null | Promise<string | null>;
  onUnauthorized?: () => void;
  fetchImpl?: typeof fetch;
}

export class HttpError extends Error {
  status: number;
  body: T.ApiError;
  constructor(status: number, body: T.ApiError) {
    super(body?.message || `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

type Query = Record<string, string | number | boolean | null | undefined>;

function qs(q?: Query): string {
  if (!q) return '';
  const p = Object.entries(q)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return p.length ? `?${p.join('&')}` : '';
}

export function createApiClient(opts: ClientOptions) {
  const f = opts.fetchImpl ?? fetch;

  async function request<R>(method: string, path: string, body?: unknown, query?: Query): Promise<R> {
    const token = await opts.getToken();
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    let payload: BodyInit | undefined;
    if (body instanceof FormData) payload = body;
    else if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
      payload = JSON.stringify(body);
    }
    const res = await f(`${opts.baseUrl}${path}${qs(query)}`, { method, headers, body: payload });
    if (res.status === 401) opts.onUnauthorized?.();
    if (res.status === 204) return undefined as R;
    const ct = res.headers.get('content-type') || '';
    const data = ct.includes('application/json') ? await res.json() : await res.text();
    if (!res.ok) throw new HttpError(res.status, typeof data === 'string' ? { message: data } : data);
    return data as R;
  }

  const get = <R>(p: string, q?: Query) => request<R>('GET', p, undefined, q);
  const post = <R>(p: string, b?: unknown, q?: Query) => request<R>('POST', p, b, q);
  const put = <R>(p: string, b?: unknown) => request<R>('PUT', p, b);
  const del = <R = void>(p: string) => request<R>('DELETE', p);

  /** Generic CRUD helper for master-data resources. */
  function crud<M, I = Partial<M>>(base: string) {
    return {
      list: (q?: Query) => get<Paginatable<M>>(base, q),
      get: (id: T.ID) => get<M>(`${base}/${id}`),
      create: (b: I) => post<M>(base, b),
      update: (id: T.ID, b: I) => put<M>(`${base}/${id}`, b),
      remove: (id: T.ID) => del(`${base}/${id}`),
    };
  }

  return {
    request,
    get,
    post,
    put,
    del,
    /** Builds an absolute URL (with token) for file downloads/printable HTML. */
    fileUrl: async (path: string, q?: Query) => {
      const token = await opts.getToken();
      const extra = qs({ ...q, token: token ?? undefined });
      if (!extra) return `${opts.baseUrl}${path}`;
      return `${opts.baseUrl}${path}${path.includes('?') ? `&${extra.slice(1)}` : extra}`;
    },

    auth: {
      login: (b: { tenant_code: string; email: string; password: string; device_name?: string }) =>
        post<{ token: string; user: T.Me }>('/auth/login', b),
      logout: () => post<void>('/auth/logout'),
      me: () => get<T.Me>('/auth/me'),
      changePassword: (b: { current_password: string; password: string; password_confirmation: string }) =>
        post<void>('/auth/password', b),
    },

    dashboard: {
      employee: () => get<T.EmployeeDashboard>('/dashboard/employee'),
      manager: () => get<T.ManagerDashboard>('/dashboard/manager'),
      hr: () => get<T.HrDashboard>('/dashboard/hr'),
    },

    org: {
      legalEntities: crud<T.OrgUnit>('/legal-entities'),
      locations: crud<T.Location>('/locations'),
      departments: crud<T.OrgUnit>('/departments'),
      designations: crud<T.OrgUnit>('/designations'),
      grades: crud<T.Grade>('/grades'),
      costCentres: crud<T.OrgUnit>('/cost-centres'),
      chart: (root?: T.ID) => get<T.OrgChartNode[]>('/org-chart', { root }),
    },

    employees: {
      list: (q?: Query) => get<T.Paginated<T.Employee>>('/employees', q),
      get: (id: T.ID) => get<T.Employee>(`/employees/${id}`),
      create: (b: T.EmployeeInput) => post<T.Employee>('/employees', b),
      update: (id: T.ID, b: T.EmployeeInput) => put<T.Employee>(`/employees/${id}`, b),
      remove: (id: T.ID) => del(`/employees/${id}`),
      sensitive: (id: T.ID) => get<Pick<T.Employee, 'pan' | 'aadhaar' | 'uan' | 'esic_number' | 'bank_account_number' | 'bank_ifsc'>>(`/employees/${id}/sensitive`),
      jobs: (id: T.ID) => get<T.EmployeeJob[]>(`/employees/${id}/jobs`),
      lifecycle: (id: T.ID) => get<T.LifecycleEvent[]>(`/employees/${id}/lifecycle`),
      lifecycleAction: (id: T.ID, b: { type: T.LifecycleEvent['type']; effective_date: T.ISODate; payload?: Record<string, unknown> }) =>
        post<T.LifecycleEvent>(`/employees/${id}/lifecycle`, b),
      documents: (id: T.ID) => get<T.EmployeeDocument[]>(`/employees/${id}/documents`),
      uploadDocument: (id: T.ID, form: FormData) => post<T.EmployeeDocument>(`/employees/${id}/documents`, form),
      verifyDocument: (docId: T.ID) => post<T.EmployeeDocument>(`/documents/${docId}/verify`),
      deleteDocument: (docId: T.ID) => del(`/documents/${docId}`),
      onboardingLink: (id: T.ID) => post<{ url: string; token: string }>(`/employees/${id}/onboarding-link`),
      import: (form: FormData, dryRun: boolean) => post<T.ImportResult>('/employees/import', form, { dry_run: dryRun ? 1 : 0 }),
      salary: (id: T.ID) => get<T.EmployeeSalary[]>(`/employees/${id}/salary`),
      assignSalary: (id: T.ID, b: { salary_structure_id: T.ID; ctc: number; effective_from: T.ISODate }) =>
        post<T.EmployeeSalary>(`/employees/${id}/salary`, b),
    },
    customFields: crud<T.CustomFieldDefinition>('/custom-fields'),
    letterTemplates: {
      ...crud<T.LetterTemplate>('/letter-templates'),
      generate: (id: T.ID, employee_id: T.ID) => post<{ html: string }>(`/letter-templates/${id}/generate`, { employee_id }),
    },
    onboarding: {
      get: (token: string) => get<{ employee: T.Employee; tenant: Pick<T.Tenant, 'name'> }>(`/onboarding/${token}`),
      submit: (token: string, form: FormData) => post<{ ok: true }>(`/onboarding/${token}`, form),
    },

    attendance: {
      shifts: crud<T.Shift>('/shifts'),
      rosters: crud<T.Roster>('/rosters'),
      punch: (form: FormData) => post<{ punch: T.Punch; day: T.AttendanceDay | null }>('/attendance/punch', form),
      today: () => get<{ day: T.AttendanceDay | null; punches: T.Punch[]; shift: T.Shift | null }>('/attendance/today'),
      my: (month: string) => get<T.AttendanceDay[]>('/attendance/my', { month }),
      employee: (id: T.ID, month: string) => get<T.AttendanceDay[]>(`/attendance/employees/${id}`, { month }),
      team: (date: T.ISODate) => get<{ employee: T.EmployeeSummary; day: T.AttendanceDay | null }[]>('/attendance/team', { date }),
      process: (b: { date_from: T.ISODate; date_to: T.ISODate; employee_id?: T.ID }) => post<{ processed: number }>('/attendance/process', b),
      regularisations: (q?: Query) => get<T.Paginated<T.Regularisation>>('/regularisations', q),
      regularise: (b: { date: T.ISODate; in_time?: string; out_time?: string; reason: string }) => post<T.Regularisation>('/regularisations', b),
      locks: () => get<T.AttendanceLock[]>('/attendance/locks'),
      lock: (period: string) => post<T.AttendanceLock & { inputs_generated: number }>('/attendance/locks', { period }),
      unlock: (id: T.ID) => del(`/attendance/locks/${id}`),
      devices: crud<T.BiometricDevice>('/biometric-devices'),
    },

    leave: {
      types: crud<T.LeaveType>('/leave-types'),
      policies: crud<T.LeavePolicy>('/leave-policies'),
      calendars: crud<T.HolidayCalendar>('/holiday-calendars'),
      addHoliday: (calendarId: T.ID, b: Omit<T.Holiday, 'id' | 'holiday_calendar_id'>) => post<T.Holiday>(`/holiday-calendars/${calendarId}/holidays`, b),
      deleteHoliday: (id: T.ID) => del(`/holidays/${id}`),
      myHolidays: (year?: number) => get<T.Holiday[]>('/holidays', { year }),
      balances: (employee_id?: T.ID) => get<T.LeaveBalance[]>('/leave/balances', { employee_id }),
      ledger: (q?: Query) => get<T.LeaveLedgerEntry[]>('/leave/ledger', q),
      requests: (q?: Query & { scope?: 'mine' | 'team' | 'all' }) => get<T.Paginated<T.LeaveRequest>>('/leave/requests', q),
      preview: (b: T.LeaveRequestInput) => post<T.LeavePreview>('/leave/requests/preview', b),
      apply: (b: T.LeaveRequestInput) => post<T.LeaveRequest>('/leave/requests', b),
      cancel: (id: T.ID) => post<T.LeaveRequest>(`/leave/requests/${id}/cancel`),
      teamCalendar: (month: string, scope?: 'team' | 'all') => get<T.TeamCalendarEntry[]>('/leave/team-calendar', { month, scope }),
      compOff: (b: { worked_date: T.ISODate; reason: string }) => post<{ id: T.ID; status: T.WorkflowStatus }>('/leave/comp-off', b),
      accrue: (period: string) => post<{ credited: number }>('/leave/accrue', { period }),
      yearEnd: (b: { year: number; dry_run: boolean }) =>
        post<{ rows: { employee: string; leave_type: string; balance: number; carry_forward: number; encashed: number; lapsed: number }[] }>('/leave/year-end', b),
    },

    workflow: {
      inbox: (q?: Query) => get<T.Paginated<T.ApprovalInboxItem>>('/approvals', q),
      approve: (stepId: T.ID, comment?: string) => post<T.ApprovalStep>(`/approvals/${stepId}/approve`, { comment }),
      reject: (stepId: T.ID, comment?: string) => post<T.ApprovalStep>(`/approvals/${stepId}/reject`, { comment }),
      instance: (id: T.ID) => get<T.WorkflowInstance>(`/workflow-instances/${id}`),
      definitions: crud<T.WorkflowDefinition>('/workflow-definitions'),
    },

    payroll: {
      components: crud<T.PayComponent>('/pay-components'),
      structures: {
        ...crud<T.SalaryStructure>('/salary-structures'),
        preview: (id: T.ID, ctc: number) => post<T.SalaryBreakdown>(`/salary-structures/${id}/preview`, { ctc }),
      },
      statutory: () => get<T.StatutoryConfig>('/statutory-config'),
      saveStatutory: (b: T.StatutoryConfig) => put<T.StatutoryConfig>('/statutory-config', b),
      inputs: (period: string, q?: Query) => get<T.Paginated<T.PayrollInput>>('/payroll/inputs', { period, ...q }),
      saveInput: (b: Partial<T.PayrollInput> & { employee_id: T.ID; period: string }) => post<T.PayrollInput>('/payroll/inputs', b),
      runs: () => get<T.PayrollRun[]>('/payroll/runs'),
      createRun: (period: string) => post<T.PayrollRun>('/payroll/runs', { period }),
      run: (id: T.ID) => get<T.PayrollRun>(`/payroll/runs/${id}`),
      runPayslips: (id: T.ID, q?: Query & { variance_only?: 0 | 1 }) => get<T.Paginated<T.Payslip>>(`/payroll/runs/${id}/payslips`, q),
      process: (id: T.ID) => post<T.PayrollRun>(`/payroll/runs/${id}/process`),
      lock: (id: T.ID) => post<T.PayrollRun>(`/payroll/runs/${id}/lock`),
      reverse: (id: T.ID) => post<T.PayrollRun>(`/payroll/runs/${id}/reverse`),
      markPaid: (id: T.ID) => post<T.PayrollRun>(`/payroll/runs/${id}/mark-paid`),
      /** file outputs: bank-file | pf-ecr | esi-challan | register | pt-report */
      outputPath: (id: T.ID, kind: 'bank-file' | 'pf-ecr' | 'esi-challan' | 'register' | 'pt-report') => `/payroll/runs/${id}/${kind}`,
      payslips: (q?: Query) => get<T.Paginated<T.Payslip>>('/payslips', q),
      payslip: (id: T.ID) => get<T.Payslip>(`/payslips/${id}`),
      payslipHtmlPath: (id: T.ID) => `/payslips/${id}/html`,
      form16Path: (employeeId: T.ID, fy: string) => `/form16/${employeeId}?fy=${fy}`,
      form24qPath: (fy: string, quarter: number) => `/form24q?fy=${fy}&quarter=${quarter}`,
      fnf: {
        list: () => get<T.FnfSettlement[]>('/fnf'),
        get: (id: T.ID) => get<T.FnfSettlement>(`/fnf/${id}`),
        compute: (b: { employee_id: T.ID; last_working_day: T.ISODate; resignation_date?: T.ISODate; notice_recovery_days?: number; other_deductions?: number; other_earnings?: number }) =>
          post<T.FnfSettlement>('/fnf', b),
        approve: (id: T.ID) => post<T.FnfSettlement>(`/fnf/${id}/approve`),
      },
    },

    tax: {
      myDeclaration: (fy?: string) => get<T.TaxDeclaration>('/tax/declarations/my', { fy }),
      saveMyDeclaration: (b: { financial_year: string; regime: 'old' | 'new'; items: T.TaxDeclarationItem[]; submit?: boolean }) =>
        put<T.TaxDeclaration>('/tax/declarations/my', b),
      declarations: (q?: Query) => get<T.Paginated<T.TaxDeclaration>>('/tax/declarations', q),
      uploadProof: (form: FormData) => post<T.TaxProof>('/tax/proofs', form),
      verifyProof: (id: T.ID, b: { status: 'approved' | 'rejected'; remarks?: string; approved_amount?: number }) => post<T.TaxProof>(`/tax/proofs/${id}/verify`, b),
      projection: (employee_id?: T.ID, regime?: 'old' | 'new') => get<T.TaxProjection>('/tax/projection', { employee_id, regime }),
    },

    ess: {
      myProfile: () => get<T.Employee>('/me/profile'),
      requestProfileChange: (changes: Record<string, unknown>) => post<T.ProfileChangeRequest>('/profile-change-requests', { changes }),
      profileChanges: () => get<T.ProfileChangeRequest[]>('/profile-change-requests'),
      notifications: (q?: Query) => get<T.Paginated<T.AppNotification>>('/notifications', q),
      readNotification: (id: T.ID) => post<void>(`/notifications/${id}/read`),
      readAll: () => post<void>('/notifications/read-all'),
      registerDevice: (b: { token: string; platform: 'ios' | 'android' | 'web' }) => post<void>('/device-tokens', b),
      tickets: {
        ...crud<T.HelpdeskTicket>('/helpdesk/tickets'),
        comment: (id: T.ID, body: string) => post<T.HelpdeskTicket>(`/helpdesk/tickets/${id}/comments`, { body }),
      },
      ask: (question: string) => post<T.AssistantReply>('/assistant/ask', { question }),
    },

    admin: {
      tenant: () => get<T.Tenant>('/tenant'),
      updateTenant: (b: Partial<T.Tenant>) => put<T.Tenant>('/tenant', b),
      roles: { ...crud<T.Role>('/roles'), clone: (id: T.ID, name: string) => post<T.Role>(`/roles/${id}/clone`, { name }) },
      permissions: () => get<T.PermissionDef[]>('/permissions'),
      users: crud<T.User, Partial<T.User> & { password?: string; role_ids?: T.ID[] }>('/users'),
      auditLogs: (q?: Query) => get<T.Paginated<T.AuditLog>>('/audit-logs', q),
      integrations: () => get<{ key: string; name: string; enabled: boolean; config: Record<string, unknown> }[]>('/integrations'),
      saveIntegration: (key: string, b: { enabled: boolean; config: Record<string, unknown> }) => put<void>(`/integrations/${key}`, b),
      billing: () => get<{ plan: string; seats: number; active_employees: number; price_per_employee: number; next_invoice_date: string }>('/billing'),
    },

    reports: {
      run: (key: string, q?: Query) => get<T.ReportResult>(`/reports/${key}`, q),
      csvPath: (key: string) => `/reports/${key}`,
    },
  };
}

/** List endpoints return either a plain array (small masters) or a paginator. */
export type Paginatable<M> = M[] | T.Paginated<M>;
export const rows = <M>(r: Paginatable<M> | undefined): M[] => (!r ? [] : Array.isArray(r) ? r : r.data);

export type ApiClient = ReturnType<typeof createApiClient>;
